/**
 * Ingredients Drawer Functionality
 * Handles opening and closing of the ingredients drawer
 */

class IngredientsDrawer {
  constructor() {
    this.drawer = document.getElementById("ingredients-drawer");
    this.overlay = document.getElementById("ingredients-overlay");
    this.closeButton = document.getElementById("ingredients-close");
    this.openButton = document.querySelector("[data-ingredients-trigger]");
    this.searchToggle = document.getElementById("ingredients-search-toggle");
    this.searchInput = document.getElementById("ingredients-search-input");
    this.noResults = document.getElementById("ingredients-no-results");

    this.openedAt = null;
    this.expandedCount = 0;
    this.searched = false;
    this.searchDebounce = null;
    this.productId = "";
    this.productName = "";

    this.init();
  }

  init() {
    if (!this.drawer) return;

    this.productId = this.drawer.dataset.productId || "";
    this.productName = this.drawer.dataset.productName || "";

    // Move ingredients button to container on page load
    this.moveButtonToContainer();

    // Bind event listeners
    this.bindEvents();

    // Initialize accordion functionality
    this.initAccordion();

    // Initialize search functionality
    this.initSearch();

    // Flush a close event if the page unloads while the drawer is open
    this.bindUnloadFlush();
  }

  pushEvent(event, payload) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...payload });
  }

  sanitizeQuery(raw) {
    if (!raw) return "";
    const cleaned = String(raw).toLowerCase().trim().slice(0, 50);
    return /^[a-z0-9 ()\-/]+$/.test(cleaned) ? cleaned : "";
  }

  ingredientCount() {
    return this.drawer.querySelectorAll(
      ".ingredients-accordion .product__accordion",
    ).length;
  }

  moveButtonToContainer() {
    const buttonWrapper = document.getElementById("ingredients-button-wrapper");
    const container = document.getElementById("ingredients-button-container");

    if (buttonWrapper && container) {
      container.appendChild(buttonWrapper);
      buttonWrapper.style.display = "";

      // Re-query the open button after moving
      this.openButton = document.querySelector("[data-ingredients-trigger]");
    }
  }

  bindEvents() {
    // Open drawer when button is clicked
    if (this.openButton) {
      this.openButton.addEventListener("click", (e) => {
        e.preventDefault();
        this.open();
      });
    }

    // Close drawer when close button is clicked
    if (this.closeButton) {
      this.closeButton.addEventListener("click", () => {
        this.close();
      });
    }

    // Close drawer when overlay is clicked
    if (this.overlay) {
      this.overlay.addEventListener("click", () => {
        this.close();
      });
    }

    // Close drawer when Escape key is pressed
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isOpen()) {
        this.close();
      }
    });
  }

  open() {
    if (!this.drawer || this.isOpen()) return;

    // Simply prevent body scroll without changing position
    document.body.style.overflow = "hidden";
    document.body.classList.add("ingredients-drawer-open");

    // Open the drawer
    this.drawer.classList.add("is-open");

    // Focus management for accessibility (without scrolling)
    if (this.closeButton) {
      this.closeButton.focus({ preventScroll: true });
    }

    // Announce to screen readers
    this.announceToScreenReader("Ingredients drawer opened");

    this.openedAt = performance.now();
    this.expandedCount = 0;
    this.searched = false;

    this.pushEvent("ingredients_drawer_open", {
      product_id: this.productId,
      product_name: this.productName,
      ingredient_count: this.ingredientCount(),
    });
  }

  close() {
    if (!this.drawer || !this.isOpen()) return;

    const timeOpenMs = this.openedAt
      ? Math.round(performance.now() - this.openedAt)
      : 0;
    const timeOpenSeconds = Math.round((timeOpenMs / 1000) * 10) / 10;
    const expandedCount = this.expandedCount;
    const searched = this.searched;

    this.drawer.classList.remove("is-open");
    document.body.classList.remove("ingredients-drawer-open");

    // Restore body scroll
    document.body.style.overflow = "";

    // Reset search
    this.clearSearch();

    // Return focus to trigger button (without scrolling)
    if (this.openButton) {
      this.openButton.focus({ preventScroll: true });
    }

    // Announce to screen readers
    this.announceToScreenReader("Ingredients drawer closed");

    this.pushEvent("ingredients_drawer_close", {
      product_id: this.productId,
      product_name: this.productName,
      time_open_ms: timeOpenMs,
      time_open_seconds: timeOpenSeconds,
      ingredients_expanded_count: expandedCount,
      searched: searched,
    });

    this.openedAt = null;
  }

  isOpen() {
    return this.drawer && this.drawer.classList.contains("is-open");
  }

  toggle() {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  initSearch() {
    if (!this.searchToggle || !this.searchInput) return;

    this.searchToggle.addEventListener("click", () => {
      const isVisible = !this.searchInput.hidden;
      if (isVisible) {
        this.searchInput.hidden = true;
        this.searchToggle.classList.remove("is-active");
        this.searchInput.value = "";
        this.filterIngredients("");
      } else {
        this.searchInput.hidden = false;
        this.searchToggle.classList.add("is-active");
        this.searchInput.focus();
      }
    });

    this.searchInput.addEventListener("input", () => {
      const value = this.searchInput.value;
      this.filterIngredients(value);

      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => this.trackSearch(value), 600);
    });
  }

  trackSearch(rawQuery) {
    const query = this.sanitizeQuery(rawQuery);
    if (!query) return;

    const visible = this.drawer.querySelectorAll(
      ".ingredients-accordion .product__accordion:not(.is-hidden)",
    ).length;

    this.searched = true;

    this.pushEvent("ingredient_search", {
      product_id: this.productId,
      query: query,
      results_count: visible,
      has_results: visible > 0,
    });
  }

  filterIngredients(query) {
    const items = this.drawer.querySelectorAll(
      ".ingredients-accordion .product__accordion",
    );
    const normalizedQuery = query.trim().toLowerCase();
    let visibleCount = 0;

    items.forEach((item) => {
      const title = item.querySelector(".accordion__title");
      if (!title) return;
      const name = title.textContent.toLowerCase();
      const matches = !normalizedQuery || name.includes(normalizedQuery);
      item.classList.toggle("is-hidden", !matches);
      if (matches) visibleCount++;
    });

    if (this.noResults) {
      this.noResults.hidden = visibleCount > 0;
    }
  }

  clearSearch() {
    if (!this.searchInput) return;
    clearTimeout(this.searchDebounce);
    this.searchInput.value = "";
    this.searchInput.hidden = true;
    this.filterIngredients("");
    if (this.searchToggle) this.searchToggle.classList.remove("is-active");
  }

  initAccordion() {
    // Get all accordion details elements in the ingredients drawer
    const accordionItems = this.drawer.querySelectorAll(
      ".ingredients-accordion details",
    );

    accordionItems.forEach((details, index) => {
      details.addEventListener("toggle", () => {
        if (details.open) {
          // Close all other accordion items
          accordionItems.forEach((otherDetails) => {
            if (otherDetails !== details && otherDetails.open) {
              otherDetails.open = false;
            }
          });

          // Force animation to restart
          const content = details.querySelector(".accordion__content");
          if (content) {
            content.style.animation = "none";
            content.offsetHeight; // Trigger reflow
            content.style.animation = "appear-down var(--duration-long) ease";
          }

          this.expandedCount += 1;
          const accordion = details.closest(".product__accordion");
          this.pushEvent("ingredient_expand", {
            product_id: this.productId,
            ingredient_name: accordion?.dataset.ingredientName || "",
            ingredient_handle: accordion?.dataset.ingredientHandle || "",
            position: index + 1,
          });
        }
      });
    });
  }

  bindUnloadFlush() {
    // If the user closes the tab/navigates away while the drawer is open,
    // synthesize a close event so dwell time isn't lost.
    window.addEventListener("pagehide", () => {
      if (this.isOpen()) this.close();
    });
  }

  announceToScreenReader(message) {
    // Create a live region for screen reader announcements
    let liveRegion = document.getElementById("ingredients-live-region");
    if (!liveRegion) {
      liveRegion = document.createElement("div");
      liveRegion.id = "ingredients-live-region";
      liveRegion.setAttribute("aria-live", "polite");
      liveRegion.setAttribute("aria-atomic", "true");
      liveRegion.style.position = "absolute";
      liveRegion.style.left = "-10000px";
      liveRegion.style.width = "1px";
      liveRegion.style.height = "1px";
      liveRegion.style.overflow = "hidden";
      document.body.appendChild(liveRegion);
    }

    liveRegion.textContent = message;
  }
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    new IngredientsDrawer();
  });
} else {
  new IngredientsDrawer();
}
